import {
  Popover,
  ActionIcon,
  Indicator,
  Stack,
  Group,
  Text,
  Button,
  ScrollArea,
  UnstyledButton,
  Tooltip,
} from '@mantine/core';
import { IconBell, IconChecks } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { UserNotification } from '../../lib/api';
import { formatReminderRelative } from '../../lib/reminders';

export function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [opened, setOpened] = useState(false);

  const countQuery = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => api.getUnreadNotificationCount(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const listQuery = useQuery({
    queryKey: ['notifications-list'],
    queryFn: () => api.getNotifications({ limit: 30 }),
    enabled: opened,
    staleTime: 15_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    void queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
  };

  const markReadMutation = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: invalidate,
  });

  const markAllMutation = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: invalidate,
  });

  const unread = countQuery.data?.count ?? 0;
  const notifications = listQuery.data?.notifications ?? [];

  const handleClick = (n: UserNotification) => {
    if (!n.readAt) markReadMutation.mutate(n.id);
    setOpened(false);
    if (n.url) navigate(n.url);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={360}
      shadow="lg"
      withinPortal
    >
      <Popover.Target>
        <Tooltip label="Benachrichtigungen">
          <Indicator
            label={unread > 99 ? '99+' : unread}
            size={16}
            color="red"
            disabled={unread === 0}
            offset={4}
          >
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              aria-label="Benachrichtigungen"
              onClick={() => setOpened((o) => !o)}
            >
              <IconBell size={20} />
            </ActionIcon>
          </Indicator>
        </Tooltip>
      </Popover.Target>

      <Popover.Dropdown p={0}>
        <Group justify="space-between" px="sm" py="xs">
          <Text size="sm" fw={600}>
            Benachrichtigungen
          </Text>
          {unread > 0 && (
            <Button
              size="compact-xs"
              variant="subtle"
              leftSection={<IconChecks size={14} />}
              loading={markAllMutation.isPending}
              onClick={() => markAllMutation.mutate()}
            >
              Alle gelesen
            </Button>
          )}
        </Group>

        <ScrollArea.Autosize mah={400}>
          {listQuery.isLoading ? (
            <Text size="xs" c="dimmed" p="sm">
              Lädt…
            </Text>
          ) : notifications.length === 0 ? (
            <Text size="xs" c="dimmed" p="sm">
              Keine Benachrichtigungen.
            </Text>
          ) : (
            <Stack gap={0}>
              {notifications.map((n) => (
                <UnstyledButton
                  key={n.id}
                  onClick={() => handleClick(n)}
                  px="sm"
                  py="xs"
                  style={{
                    borderTop: '1px solid var(--mantine-color-default-border)',
                    background: n.readAt ? undefined : 'var(--mantine-color-grape-light)',
                  }}
                >
                  <Group justify="space-between" wrap="nowrap" gap="xs">
                    <Text size="sm" fw={n.readAt ? 400 : 600} lineClamp={1}>
                      {n.title}
                    </Text>
                    <Text size="10px" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                      {formatReminderRelative(n.createdAt)}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed" lineClamp={2}>
                    {n.body}
                  </Text>
                </UnstyledButton>
              ))}
            </Stack>
          )}
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}
